import { FIELD } from "./fieldKinds.js";
import { formatDateRange, formatLocation } from "../lib/format.js";
import { COMMON_EVENT_REGISTRATION_FIELDS } from "../features/registration/formConfig.js";
import {
  compose, coverSection, aboutSection, gallerySection, chipsSection,
  speakersSection, agendaSection, rsvpSection, subscribeSection, closingSection,
} from "../features/brochure/sections.js";

// Product launch / reveal event type.
export default {
  id: "product-launch",
  category: "professional",
  label: "Product launch",
  blurb: "Reveals & launches — highlights, speakers, gallery.",
  icon: "🚀",
  wizardSteps: [
    {
      id: "basics",
      title: "The essentials",
      fields: [
        { key: "title", label: "Product / event name", kind: FIELD.TEXT, required: true, placeholder: "Acme One" },
        { key: "subtitle", label: "Tagline", kind: FIELD.TEXT, placeholder: "The future, shipped" },
        { key: "heroImage", label: "Hero image", kind: FIELD.HERO_IMAGE, previewPage: "cover" },
      ],
    },
    {
      id: "when-where",
      title: "When & where",
      fields: [
        { key: "startDate", label: "Launch date", kind: FIELD.DATE, required: true },
        { key: "endDate", label: "End date", kind: FIELD.DATE },
        { key: "venue", label: "Venue (or 'Online')", kind: FIELD.TEXT },
        { key: "city", label: "City", kind: FIELD.TEXT },
        { key: "country", label: "Country", kind: FIELD.TEXT },
        { key: "mapsUrl", label: "Google Maps link", kind: FIELD.URL, placeholder: "https://maps.google.com/...", previewPage: "cover" },
      ],
    },
    {
      id: "details",
      title: "Details, contact & registration",
      fields: [
        { key: "description", label: "About the product", kind: FIELD.TEXTAREA, previewPage: "details" },
        { key: "registrationUrl", label: "Register / RSVP link", kind: FIELD.URL, previewPage: "cover" },
        {
          key: "collectEventRegistrations",
          label: "Collect registrations inside einvite",
          kind: FIELD.TOGGLE,
          previewPage: "eventRegistration",
          description: "Adds an in-app registration form. Entries stay visible only to the creator.",
          defaultPatch: { eventRegistrationFields: COMMON_EVENT_REGISTRATION_FIELDS.map((field) => ({ ...field })) },
        },
        {
          key: "eventRegistrationFields",
          label: "Registration form builder",
          kind: FIELD.REGISTRATION_FIELDS,
          previewPage: "eventRegistration",
          showWhen: (details) => !!details.collectEventRegistrations,
        },
        { key: "contacts", label: "Contact persons", kind: FIELD.CONTACTS, previewPage: "closing" },
        {
          key: "collectPreRegistration",
          label: "Subscribe for updates",
          kind: FIELD.TOGGLE,
          previewPage: "subscribe",
          description: "A simple public form for attendees to get updates by mobile or email.",
        },
      ],
    },
    {
      id: "program",
      title: "Program & gallery",
      fields: [
        { key: "topics", label: "Highlights", kind: FIELD.CHIPS, placeholder: "Add a highlight and press Enter", previewPage: "topics" },
        { key: "speakers", label: "Speakers", kind: FIELD.SPEAKERS, previewPage: "speakers" },
        { key: "schedule", label: "Agenda", kind: FIELD.SCHEDULE, itemLabel: "segment", previewPage: "schedule" },
        { key: "customSlides", label: "Gallery slides", kind: FIELD.CUSTOM_SLIDES, previewPage: "customSlide" },
      ],
    },
  ],
  planPages(event) {
    const details = event.details || {};
    return compose(
      coverSection(event),
      aboutSection(details),
      gallerySection(details),
      chipsSection(details, { heading: "Highlights" }),
      speakersSection(details),
      agendaSection(details, "schedule", { title: "Agenda" }),
      rsvpSection(event),
      subscribeSection(event),
      closingSection(details),
    );
  },
  deriveOg(details = {}) {
    const when = formatDateRange(details.startDate, details.endDate);
    const where = formatLocation(details);
    return {
      title: details.title || "A product launch",
      description: [details.subtitle, when, where].filter(Boolean).join(" · "),
    };
  },
};
